/**
 * The referral TICKET a visitor holds — minted by the server at the first affiliate click,
 * stored with its expiry, read by every checkout.
 *
 * THE BROWSER NEVER DECIDES ATTRIBUTION. A `?ref=<code>` is only a claim; what a checkout
 * sends is `currentRef()`, a signed ticket the server issued (`modules/affiliate/tickets`)
 * that binds the code to the time of the FIRST click. The server judges the window when the
 * sale happens, against the policy in force then — so there is no window to fetch or guess
 * here, and a code typed into a URL attributes nothing without a ticket behind it.
 *
 * THE STORED VALUE IS `{ticket, expiresAt}` UNDER `solunary_ref` (contract.js's
 * `REF_STORAGE_KEY`). `expiresAt` is the server's RETENTION bound — the first click plus
 * the longest window the operator can set (`tickets.retain_until`), not the window in force
 * at the click — so a ticket is still sent when the operator lengthens the window after the
 * click. Past it no window can bring the ticket back, so it stops being sent; before it, only
 * the server's own check, against the window in force at the sale, decides. Every other shape a
 * previous build left behind (a bare code, `{code, expiresAt}`) is cleared on read: those
 * browsers attribute again on their next affiliate click, which beats honouring a value
 * the server cannot verify.
 *
 * A module-scope copy is kept beside `localStorage`, so a browser whose storage throws
 * (Safari private mode, a locked-down profile) still forwards the ticket for the rest of
 * the page's life instead of losing the attribution on the very first checkout.
 *
 * Nothing here is a hook, so it stays callable from a plain checkout handler and from a
 * Jest test without a component tree — the same separation `lib/checkoutErrors.js` uses.
 */
// "@/lib/api" like every other page: the Jest suites that touch this module already mock
// that specifier (there is no "@" alias under Jest), so a relative path would pull in the
// real axios client, which Jest cannot load.
import { api } from "@/lib/api";
import {
  AFFILIATE_PATHS, CODE_PATTERN, REF_QUERY_PARAM, REF_STORAGE_KEY, TICKET_PATTERN,
} from "../affiliate/contract";

/**
 * The click request's transport bound, in ms. The signup submit WAITS for an in-flight
 * capture (`settledCapture`), so an unbounded click — a proxy holding the connection open —
 * would hold the signup until the browser's own TCP timeout gave up, with nothing on the
 * screen. Past the bound the request is aborted and treated like any failed click: a
 * counter that missed, nothing stored, the ticket already held kept.
 */
export const CLICK_TIMEOUT_MS = 5000;

/** The page's copy of the stored value, `{ticket, expiresAt}` or `null`. */
let memory = null;

/** Resolves when every capture started so far has finished. Never rejects. */
let settled = Promise.resolve();

/** Mirrors `schemas.is_valid_code` / `CODE_PATTERN`. */
export const isValidCode = (code) =>
  typeof code === "string" && CODE_PATTERN.test(code);

const isTicket = (value) => typeof value === "string" && TICKET_PATTERN.test(value);

/** `{ticket, expiresAt}` when `value` is exactly that and both halves are readable. */
const asStored = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!isTicket(value.ticket) || typeof value.expiresAt !== "string"
      || !Number.isFinite(Date.parse(value.expiresAt))) return null;
  return { ticket: value.ticket, expiresAt: value.expiresAt };
};

const readRaw = () => {
  try {
    return window.localStorage.getItem(REF_STORAGE_KEY);
  } catch {
    return null;
  }
};

const removeRaw = () => {
  try {
    window.localStorage.removeItem(REF_STORAGE_KEY);
  } catch {
    /* Storage is unavailable; the in-memory copy is the only one and is cleared by the caller. */
  }
};

/** Forget the stored referral, in storage and in memory. */
export function clearRef() {
  memory = null;
  removeRaw();
}

const store = (value) => {
  memory = value;
  try {
    window.localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Storage is unavailable (private mode, quota): the memory copy still serves this page. */
  }
};

/**
 * The stored value, or `null`. Storage is the authority when it holds something; the
 * memory copy answers when storage holds nothing readable-and-present (unavailable, or a
 * write that failed). A present-but-foreign value is cleared, in both places.
 */
const readStored = () => {
  const raw = readRaw();
  if (raw === null) return memory;
  let parsed = null;
  try {
    parsed = asStored(JSON.parse(raw));
  } catch {
    parsed = null;
  }
  if (!parsed) {
    clearRef();
    return null;
  }
  memory = parsed;
  return parsed;
};

/**
 * The `?ref=` a URL query carries — a code or a ticket — `""` when it carries none or an
 * unusable one. Shape is judged here so nothing operator-shaped is stored or sent.
 */
export function refFromSearch(search) {
  let value;
  try {
    value = new URLSearchParams(search || "").get(REF_QUERY_PARAM);
  } catch {
    return "";
  }
  const ref = (value || "").trim();
  return isValidCode(ref) || isTicket(ref) ? ref : "";
}

/**
 * The referral ticket a checkout should send, `""` when there is none to send. Reading is
 * also when an expired value is dropped.
 */
export function currentRef() {
  const stored = readStored();
  if (!stored) return "";
  if (Date.parse(stored.expiresAt) <= Date.now()) {
    clearRef();
    return "";
  }
  return stored.ticket;
}

const captureTicket = async (ref) => {
  // A code claims a first click (and forwards the ticket already held, so the server
  // keeps the original first-click time); a ticket is our own cross-origin hop and is
  // only re-presented.
  const body = isTicket(ref) ? { ticket: ref } : { code: ref };
  const held = currentRef();
  if (!isTicket(ref) && held) body.ticket = held;
  let data;
  try {
    ({ data } = await api.post(AFFILIATE_PATHS.clicks, body, { timeout: CLICK_TIMEOUT_MS }));
  } catch {
    // A refused, rate-limited or timed-out click is a counter that missed, never
    // something to show.
    return "";
  }
  // A response with no usable ticket (disabled program, unknown code, an index.html from
  // a proxy) leaves whatever is already held alone.
  const next = asStored({ ticket: data?.ticket, expiresAt: data?.expiresAt });
  if (!next) return "";
  store(next);
  return next.ticket;
};

/**
 * Capture the referral this URL carries.
 *
 * @param {string} search `location.search`
 * @returns {Promise<string>} the ticket that was STORED, `""` when nothing was. Never
 *   rejects. The latest capture is also awaitable through `settledCapture()`.
 */
export function captureFromLocation(search) {
  const ref = refFromSearch(search);
  if (!ref) return Promise.resolve("");
  const capture = captureTicket(ref);
  settled = Promise.all([settled, capture]).then(() => undefined);
  return capture;
}

/**
 * Resolves once every capture started so far has finished (immediately when none is
 * pending), so at the latest `CLICK_TIMEOUT_MS` after the last capture started. A page that
 * reads `currentRef()` right after a `?ref=` landing — the signup form — awaits this first,
 * so a submit that races the click's answer still carries the ticket.
 */
export const settledCapture = () => settled;

export default { CLICK_TIMEOUT_MS, captureFromLocation, currentRef, clearRef, settledCapture };
